import VisibilityIcon from "@mui/icons-material/Visibility";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  AVERAGE_CTR,
  GOOGLE,
  LINKEDIN,
  META,
  RESULTS,
  TIKTOK,
  TOTAL_CLICKS,
  TOTAL_IMPRESSIONS,
  TOTAL_SPEND,
} from "../../i18n/keysTranslations";
import { logoPlatform } from "../../utils/string";
import { buildTwoPointSparkline } from "./buildTwoPointSparkline";

const PLATFORM_I18N_KEYS = {
  meta: META,
  google: GOOGLE,
  tiktok: TIKTOK,
  linkedin: LINKEDIN,
};

export const calcGrowth = (current, previous) => {
  if (previous === null || previous === undefined) return null;
  if (current === null || current === undefined) return null;
  if (Number(previous) === 0 && Number(current) > 0) return 100;
  if (Number(previous) <= 0) return null;

  const growth =
    ((Number(current) - Number(previous)) / Number(previous)) * 100;
  return isFinite(growth) ? growth : null;
};

/** Sparkline stroke color follows the period-over-period delta badge, not intra-period shape. */
export const growthToSparklineTrend = (growth) => {
  if (growth === null || growth === undefined || growth === "") {
    return "stable";
  }

  const numericGrowth = Number(growth);
  if (!Number.isFinite(numericGrowth) || numericGrowth === 0) {
    return "stable";
  }

  return numericGrowth > 0 ? "up" : "down";
};

const getPlatformDisplayName = (platform, t) => {
  const key = PLATFORM_I18N_KEYS[String(platform).toLowerCase()];
  if (key) return t(key);
  const raw = String(platform);
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

/**
 * Builds StatisticCard config array from aggregated totals.
 * @param {Object} params
 * @param {Object} params.totals - Current period totals
 * @param {Object} [params.previousTotals] - Previous period totals
 * @param {boolean} params.showPreviousPeriod
 * @param {Function} params.t - i18n translate function
 * @returns {Array} cards config for StatisticCard
 */
const attachInterpolatedSparkline = (card, showPreviousPeriod) => {
  if (!showPreviousPeriod) {
    return card;
  }

  const sparklineValues = buildTwoPointSparkline(
    card.previousValue,
    card.value
  );

  if (!sparklineValues.length) {
    return card;
  }

  return {
    ...card,
    sparklineValues,
    sparklineTrend: growthToSparklineTrend(card.growth),
  };
};

export const buildStatisticsCardsFromTotals = ({
  totals = {},
  previousTotals = {},
  showPreviousPeriod = false,
  t,
}) => {
  const defaultEmpty = 0;
  const statisticsCards = [];

  const currentCtrNum =
    totals.impressions > 0 && totals.clicks >= 0
      ? (totals.clicks / totals.impressions) * 100
      : defaultEmpty;

  const previousCtrNum =
    previousTotals.impressions > 0 && previousTotals.clicks > 0
      ? (previousTotals.clicks / previousTotals.impressions) * 100
      : defaultEmpty;

  const currentCtr =
    currentCtrNum !== defaultEmpty ? currentCtrNum.toFixed(2) : defaultEmpty;
  const previousCtr =
    previousCtrNum !== defaultEmpty ? previousCtrNum.toFixed(2) : defaultEmpty;

  statisticsCards.push(
    attachInterpolatedSparkline(
      {
        label: t(RESULTS),
        value: totals.mainAction || defaultEmpty,
        previousValue: showPreviousPeriod
          ? previousTotals.mainAction || defaultEmpty
          : defaultEmpty,
        growth: showPreviousPeriod
          ? calcGrowth(totals.mainAction, previousTotals.mainAction)
          : defaultEmpty,
        icon: CheckBoxIcon,
      },
      showPreviousPeriod
    )
  );

  statisticsCards.push(
    attachInterpolatedSparkline(
      {
        label: t(TOTAL_CLICKS),
        value: totals.clicks || defaultEmpty,
        previousValue: showPreviousPeriod
          ? previousTotals.clicks || defaultEmpty
          : defaultEmpty,
        growth: showPreviousPeriod
          ? calcGrowth(totals.clicks, previousTotals.clicks)
          : defaultEmpty,
        icon: AdsClickIcon,
      },
      showPreviousPeriod
    )
  );

  statisticsCards.push(
    attachInterpolatedSparkline(
      {
        label: t(AVERAGE_CTR),
        value: currentCtr || defaultEmpty,
        previousValue: showPreviousPeriod
          ? previousCtr || defaultEmpty
          : defaultEmpty,
        suffix: "%",
        growth: showPreviousPeriod
          ? calcGrowth(currentCtrNum, previousCtrNum)
          : defaultEmpty,
        icon: AdsClickIcon,
      },
      showPreviousPeriod
    )
  );

  statisticsCards.push(
    attachInterpolatedSparkline(
      {
        label: t(TOTAL_IMPRESSIONS),
        value: totals.impressions || defaultEmpty,
        previousValue: showPreviousPeriod
          ? previousTotals.impressions || defaultEmpty
          : defaultEmpty,
        growth: showPreviousPeriod
          ? calcGrowth(totals.impressions, previousTotals.impressions)
          : defaultEmpty,
        icon: VisibilityIcon,
      },
      showPreviousPeriod
    )
  );

  if (totals.spend && typeof totals.spend === "object") {
    Object.keys(totals.spend).forEach((currency) => {
      const spendAmount = totals.spend[currency];
      const previousSpendAmount = showPreviousPeriod
        ? previousTotals.spend?.[currency] || defaultEmpty
        : defaultEmpty;

      if (spendAmount > 0) {
        const platformBreakdown = [];
        const spendByPlatform = totals.spendByPlatform;

        if (spendByPlatform && typeof spendByPlatform === "object") {
          Object.keys(spendByPlatform)
            .sort()
            .forEach((platform) => {
              const amount = spendByPlatform[platform]?.[currency];
              if (amount > 0) {
                platformBreakdown.push({
                  name: getPlatformDisplayName(platform, t),
                  amount,
                  logoSrc: logoPlatform(platform),
                });
              }
            });
        }

        statisticsCards.push(
          attachInterpolatedSparkline(
            {
              label: `${t(TOTAL_SPEND)} (${currency})`,
              value: spendAmount,
              previousValue: showPreviousPeriod
                ? previousSpendAmount
                : defaultEmpty,
              currency,
              growth: showPreviousPeriod
                ? calcGrowth(spendAmount, previousSpendAmount)
                : defaultEmpty,
              icon: MonetizationOnIcon,
              ...(platformBreakdown.length > 0 ? { platformBreakdown } : {}),
            },
            showPreviousPeriod
          )
        );
      }
    });
  }

  return statisticsCards;
};

export const buildDefaultLoadingStatisticsCards = (t) => [
  {
    label: t(RESULTS),
    value: null,
    previousValue: null,
    growth: null,
    icon: CheckBoxIcon,
  },
  {
    label: t(TOTAL_CLICKS),
    value: null,
    previousValue: null,
    growth: null,
    icon: AdsClickIcon,
  },
  {
    label: t(AVERAGE_CTR),
    value: null,
    previousValue: null,
    suffix: "%",
    growth: null,
    icon: AdsClickIcon,
  },
  {
    label: t(TOTAL_IMPRESSIONS),
    value: null,
    previousValue: null,
    growth: null,
    icon: VisibilityIcon,
  },
  {
    label: t(TOTAL_SPEND),
    value: null,
    previousValue: null,
    currency: "USD",
    growth: null,
    icon: MonetizationOnIcon,
  },
];

export default buildStatisticsCardsFromTotals;
